import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { HelpButton } from '@/components/HelpButton';
import { useOwnership } from '@/components/OwnerBadge';
import { EmptyState } from '@/components/EmptyState';
import { HelpText } from '@/components/HelpText';
import { Modal } from '@/components/Modal';
import { useToast } from '@/components/Toast';
import {
  ALL_PAYMENT_METHODS,
  PAYMENT_METHOD_LABEL,
  type AccountWithBalance,
  type Direction,
  type PaymentMethod,
  createTransaction,
  deleteTransaction,
  fmtBRL,
  fmtDate,
  listAccounts,
  listCategories,
  listTransactions,
  type TransactionFilters,
} from '@/lib/finance';

export function TransactionsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<TransactionFilters>({ limit: 50, offset: 0 });
  const [open, setOpen] = useState(false);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setOpen(true);
      const sp = new URLSearchParams(searchParams);
      sp.delete('new');
      setSearchParams(sp, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const txQ = useQuery({
    queryKey: ['transactions', filters],
    queryFn: () => listTransactions(filters),
  });
  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const categoriesQ = useQuery({ queryKey: ['categories'], queryFn: listCategories });

  const accountById = (id: string) => accountsQ.data?.find((a) => a.id === id);
  const categoryById = (id: string | null) =>
    id ? categoriesQ.data?.find((c) => c.id === id) : null;

  const activeFilterCount = countActive(filters);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Transações</h1>
          <HelpButton topic="transacoes" />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFiltersDrawerOpen(true)}
            className="tap rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            Filtros{activeFilterCount > 0 && ` (${activeFilterCount})`}
          </button>
          <button
            onClick={() => setOpen(true)}
            className="tap hidden rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white sm:inline-block"
          >
            + lançamento
          </button>
        </div>
      </header>

      {txQ.isLoading && <p className="text-slate-500">carregando…</p>}

      {txQ.data && txQ.data.items.length === 0 && (
        <EmptyState
          title="Nenhum lançamento ainda"
          hint="Toque no + pra registrar uma despesa ou receita."
          icon="↕"
          action={
            <button
              onClick={() => setOpen(true)}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Novo lançamento
            </button>
          }
        />
      )}

      {/* mobile: cards */}
      <ul className="space-y-2 sm:hidden">
        {txQ.data?.items.map((t) => {
          const isOut = t.direction === 'out';
          const cat = categoryById(t.category_id);
          return (
            <li
              key={t.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{t.description}</p>
                <p className="text-[11px] text-slate-500">
                  {fmtDate(t.effective_date)}
                  {cat && <> · {cat.name}</>}
                  {accountById(t.account_id) && <> · {accountById(t.account_id)?.name}</>}
                  {t.payment_method && <> · {PAYMENT_METHOD_LABEL[t.payment_method]}</>}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span
                  className={`text-sm font-semibold ${isOut ? 'text-red-600' : 'text-emerald-700'}`}
                >
                  {isOut ? '−' : '+'} {fmtBRL(t.amount)}
                </span>
                <button
                  onClick={() => {
                    if (confirm('Excluir lançamento?')) {
                      deleteTransaction(t.id)
                        .then(() => {
                          toast.success('excluído');
                          qc.invalidateQueries({ queryKey: ['transactions'] });
                          qc.invalidateQueries({ queryKey: ['accounts'] });
                          qc.invalidateQueries({ queryKey: ['cards'] });
                          qc.invalidateQueries({ queryKey: ['dashboard'] });
                        })
                        .catch((e: Error) => toast.error(e.message));
                    }
                  }}
                  className="text-[10px] text-slate-400 hover:text-red-600"
                >
                  excluir
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* desktop: tabela */}
      <section className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm sm:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-4 py-2">data</th>
              <th className="px-4 py-2">descrição</th>
              <th className="px-4 py-2">categoria</th>
              <th className="px-4 py-2">conta</th>
              <th className="px-4 py-2">método</th>
              <th className="px-4 py-2 text-right">valor</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {txQ.data?.items.map((t) => {
              const cat = categoryById(t.category_id);
              const isOut = t.direction === 'out';
              return (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{fmtDate(t.effective_date)}</td>
                  <td className="px-4 py-2">{t.description}</td>
                  <td className="px-4 py-2 text-slate-600">{cat?.name ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {accountById(t.account_id)?.name ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {t.payment_method ? PAYMENT_METHOD_LABEL[t.payment_method] : '—'}
                  </td>
                  <td
                    className={`px-4 py-2 text-right font-medium ${
                      isOut ? 'text-red-600' : 'text-emerald-700'
                    }`}
                  >
                    {isOut ? '−' : '+'} {fmtBRL(t.amount)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => {
                        if (confirm('Excluir lançamento?')) {
                          deleteTransaction(t.id)
                            .then(() => {
                              toast.success('excluído');
                              qc.invalidateQueries({ queryKey: ['transactions'] });
                              qc.invalidateQueries({ queryKey: ['accounts'] });
                              qc.invalidateQueries({ queryKey: ['cards'] });
                              qc.invalidateQueries({ queryKey: ['dashboard'] });
                            })
                            .catch((e: Error) => toast.error(e.message));
                        }
                      }}
                      className="text-xs text-slate-400 hover:text-red-600"
                    >
                      excluir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {(txQ.data?.total ?? 0) > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs text-slate-500">
          <span>
            {txQ.data?.total} lançamento{txQ.data?.total === 1 ? '' : 's'}
          </span>
          <div className="flex gap-2">
            <button
              disabled={(filters.offset ?? 0) === 0}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 50)),
                }))
              }
              className="tap rounded border border-slate-300 px-2 py-0.5 disabled:opacity-50"
            >
              ← anterior
            </button>
            <button
              disabled={(txQ.data?.items.length ?? 0) < (filters.limit ?? 50)}
              onClick={() =>
                setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 50) }))
              }
              className="tap rounded border border-slate-300 px-2 py-0.5 disabled:opacity-50"
            >
              próxima →
            </button>
          </div>
        </div>
      )}

      <Modal open={filtersDrawerOpen} title="Filtros" onClose={() => setFiltersDrawerOpen(false)}>
        <div className="flex flex-col gap-4 text-sm">
          <Field label="Conta">
            <select
              value={filters.account_id ?? ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, account_id: e.target.value || undefined, offset: 0 }))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              <option value="">todas</option>
              {accountsQ.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Categoria">
            <select
              value={filters.category_id ?? ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, category_id: e.target.value || undefined, offset: 0 }))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              <option value="">todas</option>
              {categoriesQ.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.parent_id ? `↳ ${c.name}` : c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Método">
            <select
              value={filters.payment_method ?? ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  payment_method: (e.target.value as PaymentMethod) || undefined,
                  offset: 0,
                }))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              <option value="">todos</option>
              {ALL_PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABEL[m]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Direção">
            <select
              value={filters.direction ?? ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  direction: (e.target.value as Direction) || undefined,
                  offset: 0,
                }))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              <option value="">todas</option>
              <option value="in">receitas</option>
              <option value="out">despesas</option>
              <option value="transfer">transferências</option>
            </select>
          </Field>
          <Field label="De">
            <input
              type="date"
              value={filters.date_from ?? ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, date_from: e.target.value || undefined, offset: 0 }))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
          <Field label="Até">
            <input
              type="date"
              value={filters.date_to ?? ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, date_to: e.target.value || undefined, offset: 0 }))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
          <Field label="Buscar">
            <input
              value={filters.search ?? ''}
              placeholder="descrição…"
              onChange={(e) =>
                setFilters((f) => ({ ...f, search: e.target.value || undefined, offset: 0 }))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>

          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => setFilters({ limit: 50, offset: 0 })}
              className="tap rounded-md border border-slate-300 px-4 py-2"
            >
              limpar
            </button>
            <button
              type="button"
              onClick={() => setFiltersDrawerOpen(false)}
              className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white"
            >
              aplicar
            </button>
          </div>
        </div>
      </Modal>

      <CreateTxModal
        open={open}
        onClose={() => setOpen(false)}
        accounts={accountsQ.data ?? []}
        categories={categoriesQ.data ?? []}
        onCreated={() => {
          setOpen(false);
          toast.success('lançamento salvo');
          qc.invalidateQueries({ queryKey: ['transactions'] });
          qc.invalidateQueries({ queryKey: ['accounts'] });
          qc.invalidateQueries({ queryKey: ['cards'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
        }}
      />
    </div>
  );
}

function countActive(f: TransactionFilters): number {
  let n = 0;
  if (f.account_id) n++;
  if (f.category_id) n++;
  if (f.payment_method) n++;
  if (f.direction) n++;
  if (f.status) n++;
  if (f.date_from) n++;
  if (f.date_to) n++;
  if (f.search) n++;
  if (f.only_mine) n++;
  if (f.only_shared) n++;
  return n;
}

function CreateTxModal({
  open,
  onClose,
  onCreated,
  accounts,
  categories,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  accounts: AccountWithBalance[];
  categories: { id: string; name: string; kind: string; parent_id: string | null }[];
}) {
  const { myId, firstName } = useOwnership();
  const [direction, setDirection] = useState<'in' | 'out'>('out');
  const [method, setMethod] = useState<PaymentMethod | ''>('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [isShared, setIsShared] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // contas que aceitam o método escolhido
  const eligibleAccounts = useMemo(() => {
    if (!method) return [];
    return accounts.filter((a) => a.supported_methods.includes(method));
  }, [method, accounts]);

  // auto-seleciona conta quando só há uma elegível
  useEffect(() => {
    if (eligibleAccounts.length === 1) {
      setAccountId(eligibleAccounts[0].id);
    } else if (!eligibleAccounts.find((a) => a.id === accountId)) {
      setAccountId('');
    }
  }, [eligibleAccounts, accountId]);

  const filteredCategories = categories.filter(
    (c) => c.kind === direction || c.kind === 'both',
  );

  const mutation = useMutation({
    mutationFn: createTransaction,
    onSuccess: () => {
      setMethod('');
      setDescription('');
      setAmount('');
      setCategoryId('');
      setAccountId('');
      setIsShared(false);
      setNotes('');
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!method) {
      setError('Escolha como pagou.');
      return;
    }
    if (!accountId) {
      setError('Escolha a conta.');
      return;
    }
    mutation.mutate({
      description,
      amount,
      direction,
      account_id: accountId,
      effective_date: effectiveDate,
      category_id: categoryId || null,
      payment_method: method,
      is_shared: isShared,
      notes: notes || null,
    });
  }

  // métodos a mostrar: todos exceto que nenhuma conta suporta? Mostramos todos; filtragem na conta.
  const methodsToShow = ALL_PAYMENT_METHODS;

  return (
    <Modal open={open} title="Novo lançamento" onClose={onClose} size="lg">
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setDirection('out')}
            className={`tap flex-1 rounded-md border px-3 py-3 font-medium ${
              direction === 'out'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-slate-300 text-slate-600'
            }`}
          >
            despesa
          </button>
          <button
            type="button"
            onClick={() => setDirection('in')}
            className={`tap flex-1 rounded-md border px-3 py-3 font-medium ${
              direction === 'in'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-slate-300 text-slate-600'
            }`}
          >
            receita
          </button>
        </div>

        {/* 1. como pagou */}
        <Field label={direction === 'out' ? '💳 Como você pagou?' : '💳 Como recebeu?'}>
          <div className="flex flex-wrap gap-2">
            {methodsToShow.map((m) => {
              const active = method === m;
              const anyAccount = accounts.some((a) => a.supported_methods.includes(m));
              return (
                <button
                  key={m}
                  type="button"
                  disabled={!anyAccount}
                  onClick={() => setMethod(m)}
                  className={`tap rounded-full border px-3 py-1.5 text-sm ${
                    active
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : anyAccount
                        ? 'border-slate-300 text-slate-600'
                        : 'cursor-not-allowed border-slate-200 text-slate-300'
                  }`}
                >
                  {PAYMENT_METHOD_LABEL[m]}
                </button>
              );
            })}
          </div>
          {method === 'credit_card' && (
            <HelpText>A conta será o cartão de crédito (vai pra fatura).</HelpText>
          )}
        </Field>

        {/* 2. de qual conta (filtrado pelo método) */}
        {method && (
          <Field
            label={
              method === 'credit_card'
                ? '💳 Qual cartão?'
                : direction === 'out'
                  ? '💰 De qual conta saiu?'
                  : '💰 Em qual conta entrou?'
            }
          >
            <select
              required
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              <option value="">— selecionar —</option>
              {eligibleAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.owner_user_id !== myId ? ` · ${firstName(a.owner_user_id)}` : ''}
                </option>
              ))}
            </select>
            {eligibleAccounts.length === 0 && (
              <HelpText>
                Nenhuma conta aceita esse método. Edite uma conta em "Contas" pra incluir, ou
                escolha outro método.
              </HelpText>
            )}
          </Field>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Descrição">
            <input
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Ex.: "Mercado", "Salário"'
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
          <Field label="Valor (R$)">
            <input
              required
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
          <Field label="Data">
            <input
              required
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </Field>
          <Field label="Categoria">
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              <option value="">— sem categoria —</option>
              {filteredCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.parent_id ? `↳ ${c.name}` : c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <label className="tap flex items-center gap-2">
          <input
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-slate-700">marcar como compartilhada</span>
        </label>
        <Field label="Notas (opcional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base"
          />
        </Field>

        {error && <p className="text-red-600">{error}</p>}

        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'salvando…' : 'salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-slate-700">{label}</span>
      {children}
    </label>
  );
}
